import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { teamApi, salonSettingsApi } from '../../firebase/services';
import { TeamMember, SalonSettings, DEFAULT_SALON_SETTINGS } from '../../types';
import ImageUpload from './ImageUpload';
import TranslationTabs, { TranslationValues } from './TranslationTabs';

// Feminine African stylist avatars using DiceBear adventurer style
const AVATAR_OPTIONS = [
  { seed: 'Amara',    bg: 'fcd5ce', skin: '9e5622' },
  { seed: 'Fatou',    bg: 'e8c9ff', skin: '763900' },
  { seed: 'Imani',    bg: 'c9e4de', skin: '9e5622' },
  { seed: 'Adaeze',   bg: 'ffd6a5', skin: '763900' },
  { seed: 'Chioma',   bg: 'dfe7fd', skin: '9e5622' },
  { seed: 'Zara',     bg: 'fce1e4', skin: '763900' },
  { seed: 'Blessing', bg: 'd4f1c5', skin: '9e5622' },
  { seed: 'Kezia',    bg: 'cdb4db', skin: '763900' },
  { seed: 'Nadia',    bg: 'bee1e6', skin: '9e5622' },
  { seed: 'Yara',     bg: 'fde8d8', skin: '763900' },
  { seed: 'Sona',     bg: 'e2f0cb', skin: '9e5622' },
  { seed: 'Layla',    bg: 'f9c6d0', skin: '763900' },
].map(({ seed, bg, skin }) => ({
  seed,
  url: `https://api.dicebear.com/9.x/adventurer/svg?seed=${seed}&backgroundColor=${bg}&skinColor=${skin}`,
}));

interface TeamManagementProps {}

const TeamManagement: React.FC<TeamManagementProps> = () => {
  const [open, setOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [salonSettings, setSalonSettings] = useState<SalonSettings>(DEFAULT_SALON_SETTINGS);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    specialties: [] as string[],
    bio: '',
    phone: '',
    email: '',
    imageUrl: '',
    isActive: true,
    workingDays: [1, 2, 3, 4, 5] as number[],
  });

  const DAY_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const DEFAULT_HOURS = { openTime: '09:00', closeTime: '17:00' };
  const [workingHours, setWorkingHours] = useState<{ [day: number]: { openTime: string; closeTime: string } }>({
    1: { ...DEFAULT_HOURS }, 2: { ...DEFAULT_HOURS }, 3: { ...DEFAULT_HOURS },
    4: { ...DEFAULT_HOURS }, 5: { ...DEFAULT_HOURS },
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [translations, setTranslations] = useState<TranslationValues>({ fr: {}, es: {} });

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      specialties: [],
      bio: '',
      phone: '',
      email: '',
      imageUrl: '',
      isActive: true,
      workingDays: [1, 2, 3, 4, 5],
    });
    setWorkingHours({
      1: { ...DEFAULT_HOURS }, 2: { ...DEFAULT_HOURS }, 3: { ...DEFAULT_HOURS },
      4: { ...DEFAULT_HOURS }, 5: { ...DEFAULT_HOURS },
    });
    setTranslations({ fr: {}, es: {} });
    setEditingMember(null);
    setSelectedImage(null);
    setError(null);
    setSuccess(null);
  };

  const handleOpen = (member?: TeamMember) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name,
        role: member.role,
        specialties: member.specialties,
        bio: member.bio || '',
        phone: member.phone || '',
        email: member.email || '',
        imageUrl: member.imageUrl || '',
        isActive: member.isActive,
        workingDays: member.workingDays ?? [1, 2, 3, 4, 5],
      });
      // Load member's working hours, fill missing days with defaults
      const days = member.workingDays ?? [1, 2, 3, 4, 5];
      const hours: { [day: number]: { openTime: string; closeTime: string } } = {};
      days.forEach(d => {
        hours[d] = member.workingHours?.[d] ?? { ...DEFAULT_HOURS };
      });
      setWorkingHours(hours);
      setTranslations({
        fr: { role: member.translations?.fr?.role || '', bio: member.translations?.fr?.bio || '' },
        es: { role: member.translations?.es?.role || '', bio: member.translations?.es?.bio || '' },
      });
    } else {
      resetForm();
    }
    setSelectedImage(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.role.trim()) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (editingMember) {
        let imageUrl = formData.imageUrl;

        if (selectedImage) {
          setUploadingImage(true);
          imageUrl = await teamApi.uploadImage(selectedImage, editingMember.id);
          setUploadingImage(false);
        }

        const memberData = { ...formData, imageUrl, translations, workingHours };
        await teamApi.update(editingMember.id, memberData);
        setTeamMembers(prev => prev.map(member =>
          member.id === editingMember.id
            ? { ...member, ...memberData, updatedAt: new Date().toISOString() }
            : member
        ));
      } else {
        const memberData = { ...formData, imageUrl: formData.imageUrl || '', translations, workingHours };
        const memberId = await teamApi.create(memberData);

        let finalImageUrl = memberData.imageUrl;

        if (selectedImage) {
          setUploadingImage(true);
          finalImageUrl = await teamApi.uploadImage(selectedImage, memberId);
          setUploadingImage(false);
          await teamApi.update(memberId, { imageUrl: finalImageUrl });
        }

        const newMember: TeamMember = {
          id: memberId,
          ...memberData,
          imageUrl: finalImageUrl,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        setTeamMembers(prev => [...prev, newMember]);
      }

      handleClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Erreur lors de la sauvegarde du membre');
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) return;

    setLoading(true);
    try {
      await teamApi.delete(id);
      await loadTeamMembers();
      setSuccess('Membre supprimé avec succès');
    } catch (error) {
      console.error('Error deleting team member:', error);
      setError('Erreur lors de la suppression du membre');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedTeamMembers = teamMembers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    loadTeamMembers();
    salonSettingsApi.get().then(s => { if (s) setSalonSettings(s); }).catch(() => {});
  }, []);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const members = await teamApi.getAll();
      setTeamMembers(members);
    } catch (error) {
      console.error('Erreur lors du chargement des membres:', error);
      setError('Erreur lors du chargement des membres de l\'équipe');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Whether an avatar (dicebear URL) is currently selected
  const selectedAvatarUrl = !selectedImage && formData.imageUrl?.includes('dicebear') ? formData.imageUrl : '';

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ color: '#495057', fontWeight: 600 }}>
          <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gestion de l'Équipe
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          sx={{ textTransform: 'none' }}
        >
          Ajouter un membre
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Team Members Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Membre</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Rôle</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teamMembers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#6c757d' }}>
                  Aucun membre d'équipe trouvé. Ajoutez votre premier membre !
                </TableCell>
              </TableRow>
            ) : (
              paginatedTeamMembers.map((member) => (
                <TableRow key={member.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={member.imageUrl}
                        sx={{ mr: 2, bgcolor: '#e91e63' }}
                      >
                        {member.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {member.name}
                        </Typography>
                        {member.bio && (
                          <Typography variant="caption" color="text.secondary">
                            {member.bio.substring(0, 50)}...
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {member.role}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      {member.phone && (
                        <Typography variant="caption" display="block">
                          📞 {member.phone}
                        </Typography>
                      )}
                      {member.email && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          ✉️ {member.email}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={member.isActive ? 'Actif' : 'Inactif'}
                      color={member.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleOpen(member)}
                      size="small"
                      sx={{ color: '#1976d2' }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(member.id)}
                      size="small"
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={teamMembers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Lignes par page :"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
      />

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMember ? 'Modifier le membre' : 'Ajouter un nouveau membre'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Nom complet"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
              <TextField
                fullWidth
                label="Rôle"
                value={formData.role}
                onChange={(e) => handleChange('role', e.target.value)}
                placeholder="Ex: Coiffeuse senior, Spécialiste tresses..."
                required
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="(909) 123-4567"
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="membre@desireeafricanbraiding.com"
              />
            </Box>
            <TextField
              fullWidth
              label="Bio / Description"
              multiline
              rows={3}
              value={formData.bio}
              onChange={(e) => handleChange('bio', e.target.value)}
              placeholder="Décrivez l'expérience et les compétences du membre..."
            />
            <TranslationTabs
              fields={[
                { key: 'role', label: 'Rôle' },
                { key: 'bio', label: 'Bio / Description', multiline: true, rows: 3 },
              ]}
              values={translations}
              onChange={setTranslations}
            />

            {/* Photo upload */}
            <ImageUpload
              currentImageUrl={selectedAvatarUrl ? '' : formData.imageUrl}
              onImageSelected={(file) => {
                setSelectedImage(file);
                // Clear avatar selection when a real photo is picked
                if (formData.imageUrl?.includes('dicebear')) {
                  handleChange('imageUrl', '');
                }
              }}
              onImageRemoved={() => {
                setSelectedImage(null);
                setFormData(prev => ({ ...prev, imageUrl: '' }));
              }}
              label="Photo du membre"
            />

            {/* Avatar picker — shown only when no real photo is uploaded */}
            {!selectedImage && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.secondary' }}>
                  Ou choisissez un avatar
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {AVATAR_OPTIONS.map((avatar, i) => {
                    const isSelected = formData.imageUrl === avatar.url;
                    return (
                      <Tooltip key={avatar.seed} title={`Avatar ${i + 1}`}>
                        <Box
                          onClick={() => handleChange('imageUrl', isSelected ? '' : avatar.url)}
                          sx={{
                            position: 'relative',
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            border: '3px solid',
                            borderColor: isSelected ? 'primary.main' : 'transparent',
                            transition: 'border-color 0.15s, transform 0.15s',
                            '&:hover': { transform: 'scale(1.1)' },
                          }}
                        >
                          <Box
                            component="img"
                            src={avatar.url}
                            alt={avatar.seed}
                            sx={{ width: '100%', height: '100%', borderRadius: '50%', display: 'block' }}
                          />
                          {isSelected && (
                            <CheckCircleIcon
                              sx={{
                                position: 'absolute',
                                bottom: -4,
                                right: -4,
                                fontSize: 20,
                                color: 'primary.main',
                                bgcolor: 'white',
                                borderRadius: '50%',
                              }}
                            />
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
                {selectedAvatarUrl && (
                  <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                    Avatar sélectionné — vous pouvez toujours uploader une vraie photo ci-dessus.
                  </Typography>
                )}
              </Box>
            )}

            {uploadingImage && (
              <Alert severity="info">
                Upload de la photo en cours...
              </Alert>
            )}

            {/* Jours travaillés + horaires */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Jours travaillés &amp; horaires
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {DAY_LABELS.map((label, dayIndex) => {
                  const checked = formData.workingDays.includes(dayIndex);
                  const hours = workingHours[dayIndex] ?? { ...DEFAULT_HOURS };
                  return (
                    <Box
                      key={dayIndex}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: checked ? 'primary.main' : 'divider',
                        bgcolor: checked ? 'primary.50' : 'transparent',
                        transition: 'all 0.15s',
                      }}
                    >
                      {/* Day toggle */}
                      <Box
                        onClick={() => {
                          const next = checked
                            ? formData.workingDays.filter(d => d !== dayIndex)
                            : [...formData.workingDays, dayIndex].sort();
                          handleChange('workingDays', next);
                          if (!checked) {
                            setWorkingHours(prev => ({ ...prev, [dayIndex]: { ...DEFAULT_HOURS } }));
                          }
                        }}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', minWidth: 60 }}
                      >
                        <Checkbox
                          size="small"
                          checked={checked}
                          sx={{ p: 0 }}
                        />
                        <Typography variant="body2" fontWeight={700} color={checked ? 'primary.main' : 'text.disabled'}>
                          {label}
                        </Typography>
                      </Box>

                      {/* Hours */}
                      {checked ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <TextField
                            type="time"
                            size="small"
                            label="Début"
                            value={hours.openTime}
                            onChange={(e) => setWorkingHours(prev => ({
                              ...prev,
                              [dayIndex]: { ...hours, openTime: e.target.value }
                            }))}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              step: 1800,
                              min: salonSettings.businessHours[dayIndex]?.openTime,
                              max: salonSettings.businessHours[dayIndex]?.closeTime,
                            }}
                            helperText={`Salon: ${salonSettings.businessHours[dayIndex]?.openTime}`}
                            sx={{ width: 130 }}
                          />
                          <Typography variant="body2" color="text.secondary">→</Typography>
                          <TextField
                            type="time"
                            size="small"
                            label="Fin"
                            value={hours.closeTime}
                            onChange={(e) => {
                              const val = e.target.value;
                              const salonClose = salonSettings.businessHours[dayIndex]?.closeTime;
                              setWorkingHours(prev => ({
                                ...prev,
                                [dayIndex]: { ...hours, closeTime: salonClose && val > salonClose ? salonClose : val }
                              }));
                            }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              step: 1800,
                              min: salonSettings.businessHours[dayIndex]?.openTime,
                              max: salonSettings.businessHours[dayIndex]?.closeTime,
                            }}
                            helperText={`Max: ${salonSettings.businessHours[dayIndex]?.closeTime}`}
                            sx={{ width: 130 }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
                          Jour de congé
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                />
              }
              label="Membre actif"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} startIcon={<CancelIcon />}>
            Annuler
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button
              onClick={handleSubmit}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {editingMember ? 'Mettre à jour' : 'Ajouter'}
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px'
                }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamManagement;
